import {
  StarIcon as StarIconSolid,
  ArrowRightIcon,
  MapPinIcon,
  RectangleStackIcon,
  CalendarIcon,
  MoonIcon,
} from '@heroicons/react/24/solid';
import HotelPlaceholderImage from 'assets/hotel-placeholder.svg';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'hotel/navigation';

import { PrimaryButton, SecondaryButton } from 'components/Button';
import Image from 'components/Image';
import { SectionHeader } from 'components/SectionHeader';
import { Hotel, removeFromShortList } from 'store/groupDetailsSlice';
import { useAppSelector } from 'store/store';
import { calculateHotelNights } from 'utilities/calculateHotelNights';

const SelectedList = ({ hotels }: { hotels: Hotel[] }) => {
  const dispatch = useDispatch();

  return (
    <ul className="mt-2 space-y-4">
      {hotels.map((hotel) => (
        <li
          key={hotel.amadeusHotelId}
          className="group flex flex-col gap-4 rounded-lg bg-white p-4 shadow-sm transition-all hover:shadow-md sm:flex-row sm:justify-between sm:gap-x-6"
        >
          <div className="flex min-w-0 gap-x-4 sm:gap-x-6">
            <Image
              src={hotel?.imageUrl || HotelPlaceholderImage}
              alt={hotel.name}
              className="size-16 flex-none rounded-lg bg-gray-50 object-cover sm:size-24"
            />
            <div className="min-w-0 flex-auto">
              <div className="flex flex-col gap-1">
                <p className="text-lg font-semibold text-gray-900 sm:text-xl">
                  {hotel.name}
                </p>
                <p className="truncate text-sm text-gray-500 sm:text-base">
                  {hotel.formattedAddress}
                </p>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <div className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-2 py-1 text-base font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
                  <StarIconSolid className="size-4 text-accentGreen sm:size-5" />
                  <span>
                    {(hotel.starRating || 0) === 0 ? '-' : hotel.starRating}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    dispatch(removeFromShortList(hotel.amadeusHotelId))
                  }
                  className="rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-red-500 shadow-sm ring-1 ring-inset ring-gray-300 transition-colors hover:bg-gray-50 hover:text-red-600 sm:hidden"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
          <div className="hidden shrink-0 sm:block sm:self-center">
            <button
              type="button"
              onClick={() =>
                dispatch(removeFromShortList(hotel.amadeusHotelId))
              }
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-red-500 shadow-sm ring-1 ring-inset ring-gray-300 transition-colors hover:bg-gray-50 hover:text-red-600"
            >
              Remove
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
};

const EmptyState = ({
  searchForMoreHotels,
}: {
  searchForMoreHotels: () => void;
}) => (
  <div className="mt-8 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
    <div className="rounded-full bg-accentGreen/10 p-4">
      <RectangleStackIcon className="size-12 text-accentGreen" />
    </div>
    <h3 className="mt-4 text-xl font-semibold text-gray-900">
      No hotels in your shortlist
    </h3>
    <p className="mt-2 text-base text-gray-500">
      Start by searching for hotels to add to your shortlist
    </p>
    <SecondaryButton onClick={searchForMoreHotels} className="mt-6">
      Search for hotels
    </SecondaryButton>
  </div>
);

const ShortList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedHotels = useAppSelector(
    (state) => state.groupDetails.shortList,
  );
  const hasNoSelected = selectedHotels.length === 0;
  const hasSelected = !hasNoSelected;
  const selectedCount = selectedHotels.length;
  const { latitude, longitude, checkInDate, checkOutDate, description } =
    useAppSelector((state) => state.booking);
  const rooms = useAppSelector((state) => state.groupDetails.quantities);
  const numOfNights = calculateHotelNights(checkInDate, checkOutDate);
  const params = {
    latitude: latitude,
    longitude: longitude,
    checkInDate: checkInDate,
    checkOutDate: checkOutDate,
    description: description || '',
    roomQuantity: '1',
    guestQuantity: '1',
  };
  const searchParams = new URLSearchParams(params);

  const searchForMoreHotels = () => {
    navigate(`/search?${searchParams.toString()}`);
  };

  return (
    <div className="mb-40 mt-10 flex min-h-[calc(100vh-100px)] items-start justify-center px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-7xl flex-1">
        <div className="flex flex-col gap-12 xl:flex-row">
          {/* Left side - Hotels Selected */}
          <div className="w-full xl:w-3/4">
            <SectionHeader
              headline="Shortlist"
              className="text-2xl sm:text-xl"
            />
            {hasSelected && (
              <div className="mb-4 flex items-center justify-between">
                <span className="inline-flex items-center gap-x-1.5 rounded-md bg-accentGreen/10 px-2 py-1 text-base font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
                  <svg
                    className="size-1.5 fill-accentGreen"
                    viewBox="0 0 6 6"
                    aria-hidden="true"
                  >
                    <circle cx="3" cy="3" r="3" />
                  </svg>
                  {selectedCount} {selectedCount === 1 ? 'Hotel' : 'Hotels'}{' '}
                  Selected
                </span>
                <button
                  onClick={() =>
                    selectedHotels.forEach((hotel) =>
                      dispatch(removeFromShortList(hotel.amadeusHotelId)),
                    )
                  }
                  className="flex items-center text-base font-medium text-gray-500 hover:text-gray-700"
                >
                  Clear all
                </button>
              </div>
            )}
            {hasNoSelected ? (
              <EmptyState searchForMoreHotels={searchForMoreHotels} />
            ) : (
              <SelectedList hotels={selectedHotels} />
            )}
          </div>

          {/* Right side - Trip Details */}
          <div className="w-full xl:w-1/4">
            <div className="overflow-hidden rounded-lg bg-white shadow-sm xl:sticky xl:top-6">
              <div className="space-y-10 p-4 sm:p-6">
                <div>
                  <p className="mb-4 font-oswald text-lg font-bold text-gray-900">
                    Trip Details
                  </p>
                  <div className="mb-4 border-b border-gray-200"></div>
                  <div className="space-y-4">
                    <div>
                      <p className="mb-2 text-sm font-medium text-gray-500">
                        Location
                      </p>
                      <div className="flex items-center gap-2">
                        <MapPinIcon className="size-5 text-accentGreen" />
                        <p className="font-arimo text-base font-medium text-gray-900">
                          {description}
                        </p>
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 text-sm font-medium text-gray-500">
                        Dates
                      </p>
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="size-5 text-accentGreen" />
                        <p className="font-arimo text-base font-medium text-gray-900">
                          {checkInDate}
                        </p>
                        <ArrowRightIcon className="size-5 text-gray-900" />
                        <p className="font-arimo text-base font-medium text-gray-900">
                          {checkOutDate}
                        </p>
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 text-sm font-medium text-gray-500">
                        Total nights
                      </p>
                      <div className="flex items-center gap-2">
                        <MoonIcon className="size-5 text-accentGreen" />
                        <p className="font-arimo text-base font-medium text-gray-900">
                          {numOfNights}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="mb-4 font-oswald text-lg font-bold text-gray-900">
                    Room Details
                  </p>
                  <div className="mb-4 border-b border-gray-200"></div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="mb-2 text-sm font-medium text-gray-500">
                        Single
                      </p>
                      <p className="font-arimo text-base font-medium text-gray-900">
                        {rooms.single}
                      </p>
                    </div>

                    <div>
                      <p className="mb-2 text-sm font-medium text-gray-500">
                        Double
                      </p>
                      <p className="font-arimo text-base font-medium text-gray-900">
                        {rooms.double}
                      </p>
                    </div>

                    <div>
                      <p className="mb-2 text-sm font-medium text-gray-500">
                        Suite
                      </p>
                      <p className="font-arimo text-base font-medium text-gray-900">
                        {rooms.suite}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 space-y-2">
                  <PrimaryButton
                    className="w-full"
                    onClick={() => navigate('/group_request')}
                  >
                    Next: Request Details
                  </PrimaryButton>
                  <SecondaryButton
                    onClick={searchForMoreHotels}
                    className="w-full"
                  >
                    Search for more hotels
                  </SecondaryButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShortList;
